import { Flex } from '@web/components/atoms/Layout'
import { CommentsLayout, type Comments as CommentsProps } from '@web/graphql-components'
import Comment, { CommentProps } from './Comment'
import { animate, useReducedMotion, type AnimationPlaybackControls } from 'framer-motion'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

type AnimatedCommentsProps = CommentsProps & {
  comments: CommentProps[]
  isMobile?: boolean
  zoom?: number
}

const GAP = 16
const COMMENT_WIDTH = 270
const COMMENT_HEIGHT = 112
const DEFAULT_SPEED = 120
const RESTART_DELAY = 1
const PLAYBACK_EASE_SECONDS = 0.4
const LEGACY_SPEEDS: Record<string, number> = {
  '40': 90,
  '70': 120,
  '100': 180,
}
const HOVER_PAUSE_QUERY = '(hover: hover) and (pointer: fine)'

const AnimatedComments = ({
  comments,
  layout,
  isMobile,
  styles,
  mobileStyles,
}: AnimatedCommentsProps) => {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const setRef = useRef<HTMLDivElement | null>(null)
  const playbackRateRef = useRef(1)
  const playbackTweenRef = useRef<AnimationPlaybackControls | null>(null)
  const playbackTokenRef = useRef(0)
  const measuredRef = useRef({ viewportSize: 0, setSize: 0 })
  const offsetRef = useRef<number | null>(null)
  const interactionRef = useRef({ hover: false, focus: false })
  const hasEnteredRef = useRef(false)
  const introKeyRef = useRef('')
  const reducedMotion = useReducedMotion()
  const [overflows, setOverflows] = useState(false)

  const isHorizontalLayout = (isMobile ? (mobileStyles?.layout ?? layout) : layout) === CommentsLayout.Horizontal
  const axis = isHorizontalLayout ? 'x' : 'y'
  const savedSpeed = String(isMobile ? (mobileStyles?.speed ?? styles?.speed) : styles?.speed)
  const speed = LEGACY_SPEEDS[savedSpeed] ?? (Number(savedSpeed) || DEFAULT_SPEED)
  const commentsKey = comments.map((comment) => comment.id).join('|')

  const syncPlayback = useCallback(() => {
    const token = ++playbackTokenRef.current
    playbackTweenRef.current?.stop()

    const target = interactionRef.current.hover || interactionRef.current.focus ? 0 : 1
    const from = playbackRateRef.current
    if (from === target) {
      return
    }

    playbackTweenRef.current = animate(from, target, {
      duration: PLAYBACK_EASE_SECONDS * Math.abs(target - from),
      ease: target === 0 ? 'easeOut' : 'easeIn',
      onUpdate: (value) => {
        if (token !== playbackTokenRef.current) {
          return
        }
        playbackRateRef.current = value
      },
    })
  }, [])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const track = trackRef.current
    const set = setRef.current

    if (!viewport || !track || !set || reducedMotion) {
      playbackTweenRef.current?.stop()
      playbackTweenRef.current = null
      if (track) {
        track.style.transform = ''
      }
      return
    }

    let frame = 0
    let motionRun = 0

    const parkOffscreen = (viewportSize: number) => {
      track.style.transform =
        axis === 'x' ? `translate3d(${viewportSize}px, 0, 0)` : `translate3d(0, ${viewportSize}px, 0)`
    }

    const start = () => {
      const viewportSize = axis === 'x' ? viewport.clientWidth : viewport.clientHeight
      const setSize = axis === 'x' ? set.offsetWidth : set.offsetHeight

      if (!viewportSize || !setSize) {
        return
      }

      const introKey = `${commentsKey}:${axis}`
      if (introKeyRef.current !== introKey) {
        introKeyRef.current = introKey
        hasEnteredRef.current = false
        offsetRef.current = null
      }

      const viewportKey = Math.round(viewportSize)
      const setKey = Math.round(setSize)
      const shouldLoop = setSize > viewportSize
      if (shouldLoop !== overflows) {
        motionRun += 1
        measuredRef.current = { viewportSize: viewportKey, setSize: setKey }
        parkOffscreen(viewportSize)
        setOverflows(shouldLoop)
        return
      }
      const previous = measuredRef.current
      if (motionRun > 0 && previous.viewportSize === viewportKey && previous.setSize === setKey) {
        return
      }

      measuredRef.current = { viewportSize: viewportKey, setSize: setKey }
      const run = ++motionRun
      const loopDistance = setSize + GAP
      const commentSize = Math.min(axis === 'x' ? COMMENT_WIDTH : COMMENT_HEIGHT, viewportSize)
      let phase: 'entrance' | 'loop' | 'pass' = shouldLoop ? (hasEnteredRef.current ? 'loop' : 'entrance') : 'pass'
      const savedOffset = offsetRef.current
      let traveled = 0
      if (savedOffset !== null && hasEnteredRef.current && shouldLoop) {
        phase = 'loop'
        traveled = ((-savedOffset % loopDistance) + loopDistance) % loopDistance
      }
      let hold = 0
      let last = performance.now()
      let painted = Number.NaN

      const applyPosition = (value: number) => {
        offsetRef.current = value
        const pixelRatio = window.devicePixelRatio || 1
        const rendered = playbackRateRef.current === 0 ? Math.round(value * pixelRatio) / pixelRatio : value
        if (rendered === painted) {
          return
        }

        painted = rendered
        track.style.transform = axis === 'x' ? `translate3d(${rendered}px, 0, 0)` : `translate3d(0, ${rendered}px, 0)`
      }

      const positionAt = (distance: number) => {
        if (phase === 'entrance') {
          return Math.max(0, viewportSize - distance)
        }

        if (phase === 'loop') {
          const wrapped = distance % loopDistance
          return wrapped === 0 ? 0 : -wrapped
        }

        return viewportSize - distance
      }

      applyPosition(positionAt(traveled))

      const tick = (now: number) => {
        if (run !== motionRun) {
          return
        }

        const dt = Math.min(0.05, (now - last) / 1000)
        last = now
        const commentsOnScreen = phase === 'loop' || traveled >= commentSize
        const rate = commentsOnScreen ? playbackRateRef.current : 1

        if (phase === 'pass' && hold > 0) {
          hold = Math.max(0, hold - dt * rate)
        } else {
          traveled += speed * rate * dt

          if (phase === 'entrance' && traveled >= viewportSize) {
            phase = 'loop'
            hasEnteredRef.current = true
            traveled = 0
          } else if (phase === 'pass' && traveled >= viewportSize + setSize) {
            traveled = 0
            hold = RESTART_DELAY
          }
        }

        applyPosition(positionAt(traveled))
        requestAnimationFrame(tick)
      }

      requestAnimationFrame(tick)
      syncPlayback()
    }

    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(start)
    }

    const observer = new ResizeObserver(schedule)
    observer.observe(viewport)
    observer.observe(set)
    start()

    return () => {
      motionRun += 1
      cancelAnimationFrame(frame)
      observer.disconnect()
      playbackTweenRef.current?.stop()
      playbackTweenRef.current = null
    }
  }, [axis, commentsKey, overflows, reducedMotion, speed, syncPlayback])

  const commentStyleProp = {
    backgroundColor: styles?.messageBackgroundColor ?? '',
    borderColor: styles?.messageBorderColor ?? '',
    borderRadius: styles?.messageBorderRadius ?? '',
    width: isHorizontalLayout ? `${COMMENT_WIDTH}px` : '100%',
    height: `${COMMENT_HEIGHT}px`,
    fundraiserLinkColor: styles?.fundraiserLinkColor,
  }

  const setStyle = {
    display: 'flex',
    columnGap: `${GAP}px`,
    rowGap: `${GAP}px`,
    ...(isHorizontalLayout
      ? { flexDirection: 'row' as const, alignItems: 'start', width: 'fit-content', height: '100%' }
      : { flexDirection: 'column' as const, alignItems: 'center', height: 'fit-content', width: '100%' }),
  }

  return (
    <Flex
      ref={viewportRef}
      w="100%"
      h="100%"
      overflow={reducedMotion ? 'auto' : 'hidden'}
      onMouseEnter={() => {
        if (!window.matchMedia(HOVER_PAUSE_QUERY).matches) {
          return
        }

        interactionRef.current.hover = true
        syncPlayback()
      }}
      onMouseLeave={() => {
        if (!interactionRef.current.hover) {
          return
        }

        interactionRef.current.hover = false
        syncPlayback()
      }}
      onFocusCapture={() => {
        interactionRef.current.focus = true
        syncPlayback()
      }}
      onBlurCapture={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
          return
        }

        interactionRef.current.focus = false
        syncPlayback()
      }}
      css={{
        maskImage: reducedMotion
          ? undefined
          : isHorizontalLayout
            ? 'linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 5%, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0) 100%)'
            : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 5%, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0) 100%)',
      }}
      position="relative"
    >
      <div ref={trackRef} style={{ position: 'relative', willChange: 'transform', ...setStyle }}>
        <div ref={setRef} style={setStyle}>
          {comments.map((comment) => (
            <Comment key={comment.id} {...comment} styles={commentStyleProp} />
          ))}
        </div>
        {overflows && !reducedMotion && (
          <div
            aria-hidden
            inert
            style={{
              position: 'absolute',
              ...setStyle,
              ...(isHorizontalLayout
                ? { left: `calc(100% + ${GAP}px)`, top: 0 }
                : { top: `calc(100% + ${GAP}px)`, left: 0 }),
            }}
          >
            {comments.map((comment) => (
              <Comment key={comment.id} {...comment} styles={commentStyleProp} />
            ))}
          </div>
        )}
      </div>
    </Flex>
  )
}

export default AnimatedComments
